import React, { useContext, useDeferredValue, useEffect, useMemo } from 'react';

import { fcApis } from '@apis/next/fcApis';
import { TextInput } from '@components/NextForms/TextInput';
import { PriceSimulatorFormContext } from '@components/utilities/PriceSimulator/helpers';
import { japanPrefectures } from '@data/cities';
import { contactStrings } from '@data/contacts';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import CircularProgress from '@material-ui/core/CircularProgress';
import { getDecodeResult } from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import { isValidJapaneseZipcode, sanitizeZipCode } from '@utils/sanitize';
import { observer } from 'mobx-react';

const strings = createStrings({
  ja: {
    inputReceiverPrompt:
      '郵便番号を入力、もしくは都道府県と市区町村を選択して納品先を指定してください',
    invalidZipcode: '郵便番号は無効です',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  priceSimulatorReceiverInputWrapper: {
    ...common.textGray700,
    ...common.middleText,
    ...common.flexColumn,
  },
  priceSimulatorReceiverInputContainer: {
    display: 'grid',
    gridTemplateColumns: 'minmax(auto, 1fr) minmax(auto, 1fr) minmax(auto, 1fr) auto',
    columnGap: 16,
    maxWidth: 640,
  },
}));

export const PriceSimulatorReceiverInput: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { form } = useContext(PriceSimulatorFormContext);
  const { fields } = form.root.fields.receiver;

  const pref = useDeferredValue(getDecodeResult(fields.pref.decodeResult) || '');
  const sanitizedZipcode = useDebouncedValue(
    () => sanitizeZipCode(getDecodeResult(fields.zip_code.decodeResult) || ''),
    200,
    [fields.zip_code.decodeResult],
  );

  const citiesByPrefQuery = fcApis.useGet(
    '/fc/pref-and-city',
    {
      queries: {
        pref,
      },
    },
    {
      enabled: !!pref,
      staleTime: Infinity,
    },
  );

  const prefAndCityByZipcodeQuery = fcApis.useGet(
    '/fc/pref-and-city-by-zipcode',
    {
      queries: {
        zipcode: sanitizedZipcode,
      },
    },
    {
      enabled: isValidJapaneseZipcode(sanitizedZipcode),
      staleTime: Infinity,
    },
  );

  const isFetching = citiesByPrefQuery.isFetching || prefAndCityByZipcodeQuery.isFetching;

  useEffect(() => {
    if (prefAndCityByZipcodeQuery.data) {
      fields.pref.setOutput(prefAndCityByZipcodeQuery.data.pref);
      fields.city_cd.setOutput(prefAndCityByZipcodeQuery.data.city_cd);
      fields.city.setOutput(prefAndCityByZipcodeQuery.data.city);
    } else if (prefAndCityByZipcodeQuery.error) {
      fields.zip_code.setExtraErrorMessages([strings.invalidZipcode]);
    }
  }, [prefAndCityByZipcodeQuery.data]);

  return (
    <div className={classes.priceSimulatorReceiverInputWrapper}>
      <div style={{ marginBottom: '0.5rem' }}>{strings.inputReceiverPrompt}</div>

      <div className={classes.priceSimulatorReceiverInputContainer}>
        <div>
          <TextInput
            field={fields.zip_code}
            inputProps={{ placeholder: contactStrings.zip_code }}
          />
        </div>
        <div>
          <TextInput
            field={fields.pref}
            inputOptions={{
              type: 'choose',
              options: useMemo(
                () =>
                  japanPrefectures.map(({ pref }) => ({
                    value: pref,
                    label: pref,
                  })),
                [japanPrefectures],
              ),
            }}
            onSelect={() => {
              fields.city_cd.setOutput('');
              fields.city.setOutput('');
              fields.zip_code.setOutput('');
            }}
            inputProps={{ placeholder: contactStrings.pref }}
          />
        </div>
        <div>
          <TextInput
            field={fields.city_cd}
            inputOptions={{
              type: 'choose',
              options: useMemo(
                () =>
                  (citiesByPrefQuery.data?.city ?? []).map(({ city_cd, city }) => ({
                    value: city_cd,
                    label: city,
                  })),
                [citiesByPrefQuery.data?.city],
              ),
            }}
            disabled={!citiesByPrefQuery.data?.city?.length}
            onSelect={() => {
              fields.zip_code.setOutput('');
              const city_cd = getDecodeResult(fields.city_cd.decodeResult)!;
              const city = (citiesByPrefQuery.data?.city ?? []).find(
                (item) => item.city_cd === city_cd,
              )?.city;
              if (city) {
                fields.city.setOutput(city);
              }
            }}
          />
        </div>

        <div style={{ opacity: isFetching ? 1 : 0, transition: 'opacity 0.25s', paddingTop: 7 }}>
          <CircularProgress size={16} />
        </div>
      </div>
    </div>
  );
});
